<script setup lang="ts">
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

const now = new Date()
const year = ref(now.getUTCFullYear())

// ==================
// Privacy Modal
// ==================
const openPrivacyControl = ref(false)
const privacyModalEl = ref<HTMLElement>()

const { activate: startTrap, deactivate: stopTrap } = useFocusTrap(privacyModalEl, {
  initialFocus: false,
})

const onPrivacyModal = {
  afterEnter: (el: HTMLElement) => {
    disableBodyScroll(el)
    startTrap()
  },
  beforeLeave: (el: HTMLElement) => {
    stopTrap()
    enableBodyScroll(el)
  },
}

function closePrivacyModal() {
  openPrivacyControl.value = false
}

const trexonMenu: NavItem[] = [
  {
    name: 'trexon',
    title: 'Trexon',
    to: 'https://www.trexon.com/',
  },
  {
    name: 'cicoil',
    title: 'Cicoil',
    to: 'https://www.cicoil.com/',
  },
  {
    name: 'ez-form',
    title: 'EZ Form',
    to: 'https://ezform.com/',
  },
  {
    name: 'fec',
    title: 'FEC',
    to: 'https://feccables.com/',
  },
  {
    name: 'hydro-group',
    title: 'Hydro Group',
    to: 'https://www.hydrogroup-uk.com/',
  },
  {
    name: 'intelliconnect',
    title: 'Intelliconnect',
    to: 'https://www.intelliconnectgroup.com/',
  },
  {
    name: 'pci',
    title: 'PCI',
    to: 'https://powerconnector.com/',
  },
  {
    name: '603-manufacturing',
    title: '603 Manufacturing',
    to: 'https://603manufacturing.com/',
  },
  {
    name: 'cepa',
    title: 'CEPA',
    to: 'https://cepainc.com/',
  },
]
const { menu, getNavItems } = useNav()
</script>

<template>
  <div class="grid f-gap-16-96 sm:grid-flow-col">
    <div class="flex-auto space-y-4 f-max-w-224-272">
      <IcsLogo class="w-full" />
      <div>
        <p class="text-sm/5 heading-strong whitespace-nowrap">
          Integrated Cable Systems
        </p>
        <address class="leading-5 opacity-80 not-italic">
          410 S Sunset St, Suite A<br>Longmont CO, 80501
        </address>
      </div>
      <nav>
        <div>Our Partner Companies</div>
        <ul class="flex flex-wrap gap-x-4 max-w-sm">
          <li
            v-for="item in trexonMenu"
            :key="item.name"
          >
            <NuxtLink
              :to="item.to"
              class="text-sm link inline-flex items-center media-[(pointer:coarse)]:min-h-11"
            >
              {{ item.title }}
            </NuxtLink>
          </li>
        </ul>
      </nav>
      <NuxtPicture
        src="de0b7314-498b-4d6d-b60c-89f5c6d92501"
        :img-attrs="{
          class: 'my-4',
        }"
        :placeholder="[100]"
      />
      <ButtonThemeToggle color="gray" />
    </div>
    <div>
      <ul class="gap-x-6 gap-y-8 grid grid-cols-2 2xl:grid-cols-6 lg:grid-cols-3">
        <li
          v-for="item in menu"
          :key="item.name"
        >
          <NuxtLink
            class="text-sm heading-link-gray focusable inline-flex transition items-center f-py-4-8 font-flex-400-120 @hover:heading-link-hover media-[(pointer:coarse)]:min-h-11"
            :to="item.to"
          >
            {{ item.label || item.title || item.name }} <div
              v-if="item.exit"
              class="i-carbon-launch ml-1.5 size-4.5 -translate-y-px"
            />
          </NuxtLink>
          <ul
            v-if="item.children"
            class="text-sm font-300"
          >
            <li
              v-for="child in getNavItems(item.children)"
              :key="child.name"
            >
              <NuxtLink
                class="text-sm focusable inline-flex transition items-center f-py-4-8 @hover:link media-[(pointer:coarse)]:min-h-11"
                :to="child.to"
              >
                {{ child.label || child.title || child.name }} <div
                  v-if="child.exit"
                  class="i-carbon-launch ml-1.5 size-3 -translate-y-px"
                />
              </NuxtLink>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
  <div class="be-ix-link-block" />
  <div class="text-xs mt-8 text-center space-y-1">
    <div>
      <AButton
        color="blue"
        material="empty"
        size="small"
        @click="openPrivacyControl = true"
      >
        Privacy Settings
      </AButton>
    </div>
    <div>© 2024&ndash;{{ year }} Integrated Cable Systems, a Trexon Company. All rights reserved.</div>
  </div>
  <ClientOnly>
    <Teleport to="body">
      <Transition
        name="privacymodal"
        v-on="onPrivacyModal"
      >
        <div
          v-if="openPrivacyControl"
          ref="privacyModalEl"
          class="flex items-center inset-0 justify-center fixed z-9999"
        >
          <div
            class="bg-black/50 absolute-inset"
            @click="closePrivacyModal"
          />
          <div class="mx-4 rounded-sm bg-white max-w-lg w-full shadow-xl relative dark:bg-icsjet-800">
            <button
              class="p-1 focusable cursor-pointer right-5 top-5 absolute"
              aria-label="Close privacy settings"
              @click="closePrivacyModal"
            >
              <div class="i-carbon-close size-5" />
            </button>
            <PrivacyControl @saved="closePrivacyModal" />
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>
